import { API_ENDPOINTS, apiClient } from '@/shared/apiClient';

export type AgentMissionStatus =
  | 'draft'
  | 'awaiting_approval'
  | 'approved'
  | 'running'
  | 'waiting_review'
  | 'needs_revision'
  | 'completed'
  | 'blocked'
  | 'cancelled';

export type AgentMissionTaskGate = {
  state: string;
  complete?: boolean;
  reviewed?: boolean;
  checklist?: { total: number; checked: number };
  producerRunId?: string | null;
  producerRunStatus?: string | null;
  reviewerRunId?: string | null;
  reviewerRunStatus?: string | null;
  blockers?: string[];
};

export type AgentMissionTaskState = {
  task_key?: string;
  taskKey?: string;
  node_id?: string | null;
  nodeId?: string | null;
  title: string;
  purpose?: string;
  output?: string;
  phase?: string;
  craft?: string[];
  producer_profile_id?: string;
  reviewer_profile_id?: string;
  artifact_required?: boolean;
  dependencies?: string[];
  gate?: AgentMissionTaskGate;
};

export type AgentMission = {
  missionId: string;
  projectId: string;
  workflowType: string;
  status: AgentMissionStatus;
  planRevision?: number;
  planHash?: string;
  derivedStatus?: AgentMissionStatus | string;
  plan?: Record<string, unknown>;
  validationContract?: Record<string, unknown>;
  taskGraph?: AgentMissionTaskState[];
  taskStates?: AgentMissionTaskState[];
  executionPolicy?: Record<string, unknown>;
  producerHostId?: string;
  reviewerHostId?: string;
  runIds?: string[];
  blockers?: Array<Record<string, unknown>>;
  summary?: {
    taskCount?: number;
    activeCount?: number;
    waitingReviewCount?: number;
    approvedCount?: number;
    blockerCount?: number;
    isComplete?: boolean;
  };
  approvedByUsername?: string | null;
  approvedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

type AgentMissionResponse =
  | { mission?: AgentMission | null }
  | { data?: { mission?: AgentMission | null } };

const extractMission = (response: AgentMissionResponse): AgentMission | null => (
  'mission' in response ? response.mission || null : 'data' in response ? response.data?.mission || null : null
);

const requireMission = (response: AgentMissionResponse): AgentMission => {
  const mission = extractMission(response);
  if (!mission) {
    throw new Error('Agent Mission is unavailable');
  }
  return mission;
};

export const agentMissionClient = {
  async getCurrent(projectId: string): Promise<AgentMission | null> {
    const response = await apiClient.get<AgentMissionResponse>(API_ENDPOINTS.projects.agentMissionCurrent(projectId), 'gantt');
    return extractMission(response);
  },

  async createDraft(projectId: string, payload: { workflowType?: string } = {}): Promise<AgentMission> {
    const response = await apiClient.post<AgentMissionResponse>(API_ENDPOINTS.projects.agentMissionDraft(projectId), payload, 'gantt');
    return requireMission(response);
  },

  async approve(projectId: string, missionId: string, reviewedPlan: Pick<AgentMission, 'planRevision' | 'planHash'>): Promise<AgentMission> {
    const revision = reviewedPlan.planRevision;
    if (typeof revision !== 'number' || !Number.isSafeInteger(revision) || revision < 1) {
      throw new Error('The displayed plan revision is unavailable. Refresh the mission before approving.');
    }
    const response = await apiClient.post<AgentMissionResponse>(API_ENDPOINTS.projects.agentMissionApprove(projectId, missionId), {
      approvedPlanRevision: revision,
      ...(reviewedPlan.planHash ? { planHash: reviewedPlan.planHash } : {}),
    }, 'gantt');
    return requireMission(response);
  },

  async start(projectId: string, missionId: string): Promise<AgentMission> {
    const response = await apiClient.post<AgentMissionResponse>(API_ENDPOINTS.projects.agentMissionStart(projectId, missionId), {}, 'gantt');
    return requireMission(response);
  },

  async cancel(projectId: string, missionId: string): Promise<AgentMission> {
    const response = await apiClient.post<AgentMissionResponse>(API_ENDPOINTS.projects.agentMissionCancel(projectId, missionId), {}, 'gantt');
    return requireMission(response);
  },
};
